import { fetchAuthSession, signInWithRedirect, signOut } from 'aws-amplify/auth'
import { Hub } from 'aws-amplify/utils'
import { normalizeRole } from '@/lib/admin-access'
import { configureAmplify, isCognitoConfigured } from '@/lib/amplify'
import { config } from '@/config'
import { clearPersistedAuth, setAuthToken } from '@/lib/api-client'
import { useAuthStore, type AuthUser } from '@/store/auth'

/** Cognito Hosted UI returns to redirect_uri (/) with ?code=&state=. */
export function hasOAuthCallbackParams(search = window.location.search): boolean {
  const q = new URLSearchParams(search)
  return q.has('code') || q.has('error')
}

function stripOAuthParamsFromUrl() {
  const url = new URL(window.location.href)
  url.searchParams.delete('code')
  url.searchParams.delete('state')
  url.searchParams.delete('error')
  url.searchParams.delete('error_description')
  const next = `${url.pathname}${url.search}${url.hash}`
  window.history.replaceState({}, '', next || '/')
}

function persistSession(idToken: string) {
  setAuthToken(idToken)
  localStorage.setItem('idToken', idToken)
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const part = token.split('.')[1]
    if (!part) return null
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(json) as Record<string, unknown>
  } catch {
    return null
  }
}

function userFromToken(idToken: string, fallbackEmail: string): AuthUser {
  try {
    const payload = decodeJwtPayload(idToken) ?? {}
    const custom = payload['custom:role']
    const rawGroups = payload['cognito:groups']
    const fromGroups = Array.isArray(rawGroups)
      ? rawGroups.map(String)
      : typeof rawGroups === 'string' && rawGroups
        ? [rawGroups]
        : []
    const groups = [...(custom ? [String(custom)] : []), ...fromGroups].map(normalizeRole).filter(Boolean)
    const unique = [...new Set(groups)]
    return {
      userId: (payload.sub as string) || '',
      email: (payload.email as string) || fallbackEmail,
      role: unique[0] || 'staff',
      groups: unique.length ? unique : ['staff'],
    }
  } catch {
    return { userId: '', email: fallbackEmail, role: 'staff', groups: ['staff'] }
  }
}

function clearInflightOAuthLocks() {
  const keys: string[] = []
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i)
    if (key?.includes('inflightOAuth')) keys.push(key)
  }
  for (const key of keys) localStorage.removeItem(key)
}

async function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timer = setTimeout(() => reject(new Error('auth-timeout')), ms)
      }),
    ])
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/** Redirect to Cognito Hosted UI (handles temp password / MFA challenges). */
export async function loginWithCognito(): Promise<void> {
  if (!isCognitoConfigured()) {
    throw new Error('Cognito is not configured (set VITE_COGNITO_* env vars)')
  }
  clearInflightOAuthLocks()
  configureAmplify()
  await signInWithRedirect({
    options: {
      prompt: 'LOGIN',
    },
  })
}

/** Restore session after OAuth callback or page refresh. */
export async function restoreCognitoSession(): Promise<boolean> {
  if (!isCognitoConfigured()) {
    useAuthStore.getState().setLoading(false)
    return false
  }

  configureAmplify()
  if (!hasOAuthCallbackParams()) {
    clearInflightOAuthLocks()
  }

  try {
    const session = await withTimeout(fetchAuthSession(), 8000)
    const idToken = session.tokens?.idToken?.toString()
    if (!idToken) {
      clearPersistedAuth()
      useAuthStore.getState().setUser(null)
      return false
    }

    persistSession(idToken)
    const email =
      (session.tokens?.idToken?.payload?.email as string) || fallbackEmailFromPayload(idToken)
    useAuthStore.getState().setUser(userFromToken(idToken, email))
    return true
  } catch {
    clearPersistedAuth()
    useAuthStore.getState().setUser(null)
    return false
  } finally {
    if (hasOAuthCallbackParams()) stripOAuthParamsFromUrl()
    useAuthStore.getState().setLoading(false)
  }
}

function fallbackEmailFromPayload(idToken: string): string {
  return (decodeJwtPayload(idToken)?.email as string) || ''
}

/** Hub + initial restore. Keep OAuth ?code= on `/` until this finishes. */
export function startAuthListener(): () => void {
  configureAmplify()
  const unsub = Hub.listen('auth', ({ payload }) => {
    if (payload.event === 'signInWithRedirect') {
      void restoreCognitoSession()
    }
    if (payload.event === 'signInWithRedirect_failure') {
      clearPersistedAuth()
      useAuthStore.getState().setUser(null)
      useAuthStore.getState().setLoading(false)
    }
  })
  void restoreCognitoSession()
  return unsub
}

function cognitoHostedBaseUrl(): string {
  const raw = config.cognito.domain.trim()
  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    return raw.replace(/\/$/, '')
  }
  return `https://${raw.replace(/\/$/, '')}`
}

export function getCognitoHostedLogoutUrl(): string | null {
  if (!isCognitoConfigured()) {
    return null
  }
  const logoutUri = encodeURIComponent(config.cognito.redirectSignOut)
  return `${cognitoHostedBaseUrl()}/logout?client_id=${encodeURIComponent(config.cognito.clientId)}&logout_uri=${logoutUri}`
}

export async function logoutFromCognito(): Promise<void> {
  clearPersistedAuth()
  if (isCognitoConfigured()) {
    configureAmplify()
    try {
      await signOut({ global: true })
    } catch {
      // ignore
    }
  }
  useAuthStore.getState().logout()
  const hostedLogout = getCognitoHostedLogoutUrl()
  window.location.href = hostedLogout ?? '/login'
}
